import { EuiFlexItem, EuiIcon, EuiLoadingSpinner, EuiSwitch, EuiText } from "@elastic/eui";
import React, { FC, useContext, useState } from "react";
//@ts-expect-error - Could not find a declaration file for module 'react-currency-formatter'.
import Currency from "react-currency-formatter";

import { useEditShopToPrice } from "../api/react-query/mutations/shop-to-prices";
import { ShopContext } from "../utils/Shop";
import { ActionLabel, Price } from "../utils/types";
import { priceGridStyles } from "./PriceGridStyles";

interface PriceProps {
    prices: Record<string, number | null>;
    record?: Price;
    setEditRecord?: (record: any) => void;
    getModalPrices?: (key: string, value: number, productId: string) => void;
    mode?: string;
}

interface PriceCardProps {
    label: string;
    value: number | null;
    onClick?: () => void;
    readOnly: boolean;
    deactivatedLabels?: string[];
    actionLabels?: ActionLabel[];
    isLoading?: boolean;
}

const PriceCard: FC<PriceCardProps> = ({
    label,
    value,
    onClick,
    readOnly,
    deactivatedLabels,
    actionLabels,
    isLoading,
}) => {
    if (!value) return null;

    const isDisabled = deactivatedLabels?.includes(label);
    const isDiscounted = actionLabels != null && actionLabels.find((al) => al.label === label) != null;

    return (
        <EuiFlexItem
            grow={false}
            className={[
                "priceCard",
                isDisabled ? "priceCardDisabled diagonal" : "",
                isLoading ? "priceCardLoading" : "",
            ].join(" ")}
            tabIndex={readOnly ? -1 : 0}
            onClick={onClick}
            style={{ pointerEvents: readOnly ? "none" : "auto", lineHeight: 1.2 }}
        >
            <div className="priceCardHeader">
                <strong className="priceLabel">
                    {label} {isDiscounted && "🔖"}
                </strong>
                {!readOnly &&
                    (isLoading ? (
                        <EuiLoadingSpinner size="s" className="priceTableEditIcon" />
                    ) : (
                        <EuiIcon color="primary" className="priceTableEditIcon" size="s" type="pencil" />
                    ))}
            </div>
            <EuiText color={isDiscounted ? "red" : "info"}>
                <Currency
                    quantity={isDiscounted ? actionLabels.find((al) => al.label === label)!.price : value}
                    currency="EUR"
                    locale="nl_NL"
                />
            </EuiText>
        </EuiFlexItem>
    );
};

export const PriceGrid: FC<PriceProps> = ({ prices, record, setEditRecord, getModalPrices, mode }) => {
    const [loadingKey, setLoadingKey] = useState<string | null>(null);
    const { mutate: toggleLabel } = useEditShopToPrice();
    const { shopId } = useContext(ShopContext);

    const handleToggleDeactivate = (label: string) => {
        if (!record?.kind_id) {
            console.error("No kind_id provided");
            return;
        }
        const isDeactivated = record.deactivated_labels?.includes(label);
        const newDeactivatedLabels = isDeactivated
            ? record.deactivated_labels.filter((l) => l !== label)
            : [...(record.deactivated_labels ?? []), label];

        setLoadingKey(label);
        toggleLabel(
            {
                id: record.id,
                data: {
                    active: record.active,
                    new: record.new,
                    price_id: record.price_id,
                    shop_id: shopId,
                    category_id: record.category_id,
                    kind_id: record.kind_id,
                    deactivated_labels: newDeactivatedLabels,
                    action_labels: record.action_labels,
                },
            },
            { onSettled: () => setLoadingKey(null) }
        );
    };

    const handleClick = (key: string, value: number) => {
        if (mode === "cannabis" && setEditRecord && getModalPrices && record) {
            setEditRecord(record);
            setLoadingKey(key);
            const result = getModalPrices(key, value, record.internal_product_id);
            if (result && typeof result.then === "function") {
                result.then(() => setLoadingKey(null)).catch(() => setLoadingKey(null));
            } else {
                setLoadingKey(null);
            }
        }
    };

    const unitPriority = ["g", "j", "st"]; // g first, then j, then st

    //Sort the price cards by largest amount
    const sortedPrices = Object.fromEntries(
        Object.entries(prices).sort(([keyA], [keyB]) => {
            const getUnit = (key: string) => key.match(/[a-zA-Z]+$/)?.[0] || "";
            const getNumber = (key: string) => {
                const match = key.match(/^\d+(\.\d+)?/);
                return match ? parseFloat(match[0]) : 0.0;
            };

            const unitA = getUnit(keyA);
            const unitB = getUnit(keyB);

            const unitIndexA = unitPriority.indexOf(unitA);
            const unitIndexB = unitPriority.indexOf(unitB);

            if (unitIndexA !== unitIndexB) {
                return unitIndexA - unitIndexB;
            }
            return getNumber(keyA) - getNumber(keyB);
        })
    );
    return (
        <EuiFlexItem css={priceGridStyles}>
            <div className="priceGrid">
                {Object.keys(sortedPrices).map((key) => (
                    <div key={key} className="priceCardWrapper">
                        <PriceCard
                            label={key}
                            value={prices[key]}
                            onClick={prices[key] ? () => handleClick(key, prices[key] as number) : undefined}
                            readOnly={!record}
                            deactivatedLabels={record?.deactivated_labels ?? []}
                            actionLabels={record?.action_labels ?? []}
                            isLoading={loadingKey === key}
                        />
                        {record?.kind_id && (
                            <EuiSwitch
                                css={{ paddingLeft: "4px" }}
                                label={""}
                                checked={!record.deactivated_labels?.includes(key)}
                                onChange={(e) => {
                                    e.nativeEvent.stopPropagation();
                                    handleToggleDeactivate(key);
                                }}
                                compressed
                            />
                        )}
                    </div>
                ))}
            </div>
        </EuiFlexItem>
    );
};
